import { Injectable } from '@angular/core';
import { FormGroup, FormControl, FormArray } from '@angular/forms';
import {
    NestedFormGroupType,
    FormGroupType,
} from '../../../../../../core/models/form-group-type';
import { EmissionDesignator } from '../../models/emission-designator';
import { EmissionDesignatorOperation } from '../../models/emission-designator-operation';
import { EmissionDesignatorFormService } from '../emission-designator-form/emission-designator-form.service';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { FrequencyFormModel } from '../../models/frequency-form-model';
import { BaseEmissionDesignatorOperationFormService } from '../base-emission-designator-operation-form/base-emission-designator-operation-form.service';

@Injectable({
    providedIn: 'root',
})
export class EmissionDesignatorOperationFormService extends BaseEmissionDesignatorOperationFormService<EmissionDesignatorOperation> {
    constructor(
        protected override readonly emissionDesignatorFormService: EmissionDesignatorFormService,
    ) {
        super(emissionDesignatorFormService);
    }

    getFormDefinition(): FormGroup<
        NestedFormGroupType<EmissionDesignatorOperation>
    > {
        return new FormGroup<NestedFormGroupType<EmissionDesignatorOperation>>({
            addEmissionDesignator: new FormControl<string>(''),
            addedEmissionDesignators: new FormArray<
                FormGroup<FormGroupType<EmissionDesignator>>
            >([]),
            deleteEmissionDesignator: new FormControl<string>(''),
            deletedEmissionDesignators: new FormArray<
                FormGroup<FormGroupType<EmissionDesignator>>
            >([]),
        });
    }

    getFilteredDeleteEmissionDesignatorAutoCompleteOptions(
        value: string,
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
        formArray: FormArray<FormGroup<FormGroupType<EmissionDesignator>>>,
    ): DropdownOption[] {
        let options: EmissionDesignator[] = [];

        const uniqueEmissionDesignatorsFromAllFrequencies =
            this.getUniqueEmissionDesignatorsFromAllFrequencies(frequencies);

        if (value === '') {
            options = uniqueEmissionDesignatorsFromAllFrequencies;
        } else {
            const filterValue = value!.toUpperCase();

            options = uniqueEmissionDesignatorsFromAllFrequencies.filter(
                (option) => option.code!.toUpperCase().includes(filterValue),
            );
        }

        formArray.controls.forEach((control) => {
            options = options.filter(
                (option) => option.code !== control.value.code,
            );
        });

        return options.map((option) => {
            return {
                label: option.code!,
                value: option.code!,
            } as DropdownOption;
        });
    }

    getDeleteEmissionDesignatorDropdownOptions(
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
        formArray: FormArray<FormGroup<FormGroupType<EmissionDesignator>>>,
    ): DropdownOption[] {
        let list = this.getUniqueEmissionDesignatorsFromAllFrequencies(
            frequencies,
        ).map((emissionDesignator) => {
            return {
                label: emissionDesignator.code!,
                value: emissionDesignator.code!,
            } as DropdownOption;
        });

        formArray.controls.forEach((control) => {
            list =
                this.emissionDesignatorFormService.getEmissionDesignatorsListWithoutCode(
                    list,
                    control.value.code!,
                );
        });

        return list;
    }

    getUniqueEmissionDesignatorsFromAllFrequencies(
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
    ): EmissionDesignator[] {
        const emissionDesignatorsFromAllFrequenciesSelected =
            this.emissionDesignatorFormService.getEmissionDesignatorsFromAllFrequencies(
                frequencies,
            );

        return emissionDesignatorsFromAllFrequenciesSelected.reduce(
            (accumulator, currentValue) => {
                return accumulator.concat(
                    currentValue.filter(
                        (value) =>
                            !accumulator.some(
                                (emissionDesignator) =>
                                    emissionDesignator.code === value.code,
                            ),
                    ),
                );
            },
        );
    }
}
